/**
 * Rebasing a term's quoted date ranges when a reviewer changes its month count.
 *
 * ⚠️ WHY THIS EXISTS
 *
 * Re-terming an offer in the review screen renamed the contract_terms key — `12_smooth` becomes
 * `4_smooth` — and left the date ranges alone. The term then carried a 12-month range under a
 * 4-month label, and the calculation engine rejects that: termLengthInYearsFromDates only
 * believes parsed dates when they land within MAX_TERM_LABEL_DRIFT_DAYS (15) of the month
 * count's implied end. 364 days against an implied 121 is a 243-day drift, so the dates are
 * thrown away and the term silently drops to the months-based approximation
 * `termMonths / 12 - 1/365` instead of its real day count / 365.
 *
 * Measured on prod tender bd3f75f1 (WERRIBEE RACING CLUB, terms 4/16/28/40):
 *
 *     AGL, Origin        4-month range 2026-09-01 -> 2026-12-31   121 days   weight 0.331507
 *     ALINTA, Engie,     4-month range 2026-09-01 -> 2027-08-31   364 days   weight 0.330594
 *     Momentum           (stale 12-month range kept after re-term)
 *
 * The three re-termed offers priced every figure 0.28% low against the client's model, and
 * rebasing the range to 2026-12-31 reproduces the client's number exactly ($3,427 against our
 * $3,418 on Momentum's 4-month commodity spend).
 *
 * TWO DELIBERATE LIMITS:
 *
 *   1. Ranges are rewritten, never invented. An offer that arrived with no dates keeps none, so
 *      it stays on the months-based form it uses today. Manufacturing a range would move offers
 *      onto the date-based path that were never on it — a far wider change than the bug.
 *
 *   2. Whole-year terms are still rebased. That is safe because the engine ignores dates for
 *      them outright (termLengthInYearsFromDates returns null when termMonths % 12 === 0), so
 *      the rewrite cannot alter a 12/24/36/48 figure. It runs anyway so the stored dates stop
 *      contradicting the label a reviewer just chose.
 */

const MS_PER_DAY = 86_400_000;

const YEAR_KEY = /^year_(\d+)$/;

const parseDay = (value: unknown): Date | null => {
  if (typeof value !== 'string') return null;
  const ms = Date.parse(`${value.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(ms) ? null : new Date(ms);
};

const toIso = (date: Date): string => date.toISOString().slice(0, 10);

/**
 * Calendar-month arithmetic that cannot roll into the next month.
 *
 * Plain setUTCMonth turns 31 Jan + 1 month into 3 March. Clamping to the target month's last
 * day keeps a month-end contract on month ends, which is what every retailer quotes.
 */
const addMonths = (date: Date, months: number): Date => {
  const day = date.getUTCDate();
  const firstOfTarget = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
  const daysInTarget = new Date(Date.UTC(
    firstOfTarget.getUTCFullYear(), firstOfTarget.getUTCMonth() + 1, 0,
  )).getUTCDate();
  firstOfTarget.setUTCDate(Math.min(day, daysInTarget));
  return firstOfTarget;
};

const dayBefore = (date: Date): Date => new Date(date.getTime() - MS_PER_DAY);

const dayAfter = (date: Date): Date => new Date(date.getTime() + MS_PER_DAY);

/** A Date from either shape a caller may hold — an ISO day string or a Date already. */
const asDate = (value: string | Date): Date | null => {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  return parseDay(value);
};

/** Whichever key the range sits under — both shapes occur in parsed data. */
const rangeKeyOf = (obj: any): 'date_range' | 'dateRange' | null => {
  if (obj && typeof obj === 'object') {
    if (obj.date_range && typeof obj.date_range === 'object') return 'date_range';
    if (obj.dateRange && typeof obj.dateRange === 'object') return 'dateRange';
  }
  return null;
};

const startOf = (obj: any): Date | null => {
  const key = rangeKeyOf(obj);
  if (!key) return null;
  const range = obj[key];
  return parseDay(range?.start_date ?? range?.startDate);
};

const endOf = (obj: any): Date | null => {
  const key = rangeKeyOf(obj);
  if (!key) return null;
  const range = obj[key];
  return parseDay(range?.end_date ?? range?.endDate);
};

/**
 * The contract's first day for this term: the earliest start date it carries, taken from the
 * term-level range first and then from its year slots — the same two shapes
 * termLengthInYearsFromDates reads, so the value this rebases onto is the one the engine sees.
 */
export const termContractStart = (termData: Record<string, any> | null | undefined): Date | null => {
  if (!termData || typeof termData !== 'object') return null;

  let earliest = startOf(termData);
  for (const key of Object.keys(termData)) {
    if (!YEAR_KEY.test(key)) continue;
    const slotStart = startOf(termData[key]);
    if (slotStart && (!earliest || slotStart < earliest)) earliest = slotStart;
  }
  return earliest;
};

const writeRange = (target: any, start: Date, end: Date): any => {
  const key = rangeKeyOf(target);
  if (!key) return target;
  const existing = target[key] ?? {};
  const camel = existing.startDate !== undefined || existing.endDate !== undefined;
  return {
    ...target,
    [key]: camel
      ? { ...existing, startDate: toIso(start), endDate: toIso(end) }
      : { ...existing, start_date: toIso(start), end_date: toIso(end) },
  };
};

/**
 * A term's date ranges rebuilt for `termMonths`, keeping its quoted start date.
 *
 * Year N runs from the Nth anniversary of the start to the day before the next one, and the
 * final slot is truncated to the term's own end (start + termMonths - 1 day) — which is what
 * produces the short tail an unconventional term ends on. Verified against the offers in this
 * tender that were NOT re-termed: for a 2026-09-01 start this yields 2026-12-31 for 4 months,
 * 2027-12-31 for 16, 2028-12-31 for 28 and 2029-12-31 for 40, matching AGL, Origin and Flo's
 * parsed ranges exactly.
 *
 * Returns `termData` untouched when there is no start date to rebase onto, when termMonths is
 * not a positive number, or when the term carries no ranges at all.
 */
export const rebaseTermYearRanges = <T extends Record<string, any>>(
  termData: T,
  termMonths: number,
): T => {
  if (!termData || typeof termData !== 'object') return termData;
  if (!Number.isFinite(termMonths) || termMonths <= 0) return termData;

  const start = termContractStart(termData);
  if (!start) return termData;

  const termEnd = dayBefore(addMonths(start, termMonths));
  if (termEnd < start) return termData;

  const result: Record<string, any> = { ...termData };

  // Term-level range, when the shape carries one.
  if (rangeKeyOf(result)) {
    Object.assign(result, writeRange(result, start, termEnd));
  }

  for (const key of Object.keys(result)) {
    const match = YEAR_KEY.exec(key);
    if (!match) continue;

    const slot = result[key];
    // Limit 1 — only rewrite a range that is already there.
    if (!rangeKeyOf(slot)) continue;

    const index = parseInt(match[1], 10);
    if (!Number.isFinite(index) || index < 1) continue;

    const slotStart = addMonths(start, (index - 1) * 12);
    // A slot beyond the term's own end has no range to state. Leave it as it is rather than
    // writing an inverted one; getExpectedYearsForTerm already prunes slots the term cannot
    // reach, so this is a guard, not a path.
    if (slotStart > termEnd) continue;

    const anniversaryEnd = dayBefore(addMonths(start, index * 12));
    const slotEnd = anniversaryEnd < termEnd ? anniversaryEnd : termEnd;

    result[key] = writeRange(slot, slotStart, slotEnd);
  }

  return result as T;
};

/**
 * Rate-year slots to show for a term.
 *
 * ceil(months/12) is how many CALENDAR years a term touches, which is not the same as how
 * many rate years the offer prices. A 52-month offer touches five, but the document quotes
 * four — so the fifth slot was drawn as a row with no rates and no readable date range,
 * exactly the empty "mm/dd/yyyy" row testers reported on 16/28/40/52 tenders.
 *
 * Given the term's data, only the years it actually prices are returned. With no data (a
 * brand-new term) the full span is offered so there is somewhere to type. A Smooth term that
 * prices only year_1 therefore shows one row, which is also what it means.
 */
export const getExpectedYearsForTerm = (termMonths: number, termData?: Record<string, any> | null): string[] => {
  const years = Math.max(1, Math.ceil(termMonths / 12));
  const all = Array.from({ length: years }, (_, i) => `year_${i + 1}`);
  if (!termData) return all;

  const priced = all.filter(y => {
    const d = termData[y];
    return !!d && (d.peak != null || d.offpeak != null || d.shoulder != null);
  });
  return priced.length > 0 ? priced : all;
};

/**
 * Rate-year rows to show in the OFFER EDITOR.
 *
 * ⚠️ NOT getExpectedYearsForTerm. THAT ONE PRUNES BY WHAT IS PRICED, AND THE EDITOR CANNOT.
 *
 * Pruning to priced years is right for re-terming — it is what stops a 40-to-52 move inventing
 * a year_5 nobody quoted. Driving the editor from it is a trap, because being priced is exactly
 * what the reviewer is there to change:
 *
 *   handleAddContractTerm writes year_1..year_4 as empty objects, so a new 48-month Stepped
 *   term draws four rows. Type Year 1's peak and `priced` becomes ["year_1"] — Years 2, 3 and 4
 *   vanish from the screen, and with them the only way to enter them. Testers reported it as
 *   "the other Year field disappears when rates are entered".
 *
 * A Stepped term therefore counts KEYS, not rates: typing a rate never adds or removes a year
 * key, so the row set cannot move under the reviewer. Rows run from year_1 to the highest key
 * present, which also recovers Origin offers parsed with only their last year — HOUSE OF
 * CARNIVORE's 24_stepped holds year_2 alone, and year_1 was unreachable.
 *
 * Smooth is left on getExpectedYearsForTerm untouched. One rate covers the term, so one row is
 * the truth, and the pruning there is also what keeps a 52-month offer that quotes four years
 * from drawing an empty fifth row with no readable date range.
 *
 * @param termKey - the contract_terms key: "36", "36_stepped", "36_smooth"
 */
export const getEditableYearsForTerm = (
  termKey: string,
  termData?: Record<string, any> | null,
): string[] => {
  const termMonths = parseInt(String(termKey).replace(/[^0-9].*$/, ''), 10);
  const stepped = String(termKey).endsWith('_stepped') || termData?.rate_type === 'Stepped';
  if (!stepped) return getExpectedYearsForTerm(termMonths, termData);

  const span = Math.max(1, Math.ceil((Number.isFinite(termMonths) ? termMonths : 12) / 12));
  const present = Object.keys(termData || {})
    .map(k => YEAR_KEY.exec(k))
    .filter((m): m is RegExpExecArray => m !== null)
    .map(m => parseInt(m[1], 10))
    .filter(n => Number.isFinite(n) && n >= 1);

  const count = present.length > 0 ? Math.max(...present) : span;
  return Array.from({ length: count }, (_, i) => `year_${i + 1}`);
};

/** Year keys present on a term, ascending. */
const yearKeysOf = (termData: Record<string, any>): string[] =>
  Object.keys(termData || {})
    .filter(k => /^year_\d+$/.test(k))
    .sort((a, b) => parseInt(a.slice(5), 10) - parseInt(b.slice(5), 10));

/**
 * Whole months from a start date to an end date, inclusive of both months.
 *
 * Exported so expandSteppedAgreementTerms measures a sub-term the same way removeTermYear
 * re-measures the whole one. Two rules here would disagree the moment a stub year is removed.
 */
export const inclusiveMonthSpan = (startMs: number, endMs: number): number | null => {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) return null;
  const s = new Date(startMs);
  const e = new Date(endMs);
  const months = (e.getUTCFullYear() * 12 + e.getUTCMonth())
    - (s.getUTCFullYear() * 12 + s.getUTCMonth()) + 1;
  return months > 0 ? months : null;
};

/**
 * A term with one of its rate years removed — renumbered, re-dated and re-measured.
 *
 * ⚠️ DELETING A YEAR IS FOUR CHANGES, NOT ONE.
 *
 * Origin's offer on this tender is a single 51-month STEPPED term whose first period is a
 * 3-month stub (01/10/2026 - 31/12/2026) that the tender never asked for. Removing just the
 * `year_1` object would leave a hole at slot 1, and the calculation drops any Stepped term with
 * an unpriced year — `unpricedContractYears` has no Year-1 substitute for Stepped, because
 * borrowing one would invent a quote. The offer would vanish from that term's table with only a
 * console warning. So a plain delete is worse than no delete at all.
 *
 * Removing a year therefore also:
 *   1. renumbers every later year down one, so the run stays year_1..year_N with no gap
 *   2. re-measures the term from the remaining span — dropping Origin's stub leaves
 *      01/01/2027 - 31/12/2030, which is 48 months, not 51
 *   3. rebases the date ranges onto that new length and start
 *   4. hands back the new month count so the caller can rename the contract_terms key
 *
 * Returns null when there is nothing safe to do: fewer than two years (a term must keep at
 * least one), an unknown key, or dates too incomplete to re-measure the term. The caller then
 * leaves the offer exactly as it is.
 */
export const removeTermYear = (
  termData: Record<string, any> | null | undefined,
  termMonths: number,
  yearKey: string,
): { months: number; termData: Record<string, any> } | null => {
  if (!termData || typeof termData !== 'object') return null;

  const years = yearKeysOf(termData);
  if (years.length < 2 || !years.includes(yearKey)) return null;

  const kept = years.filter(k => k !== yearKey);

  // ⚠️ RE-MEASURE FROM THE TERM'S OWN START, NOT THE FIRST SURVIVOR'S.
  //
  // Removing a rate year does not move the day the contract begins. Origin's agreement 3741888
  // runs 01/10/2026 - 31/12/2030 whether or not its opening 3-month stub is priced separately;
  // dropping the stub says "bill the opening period at the next year's rate", not "start three
  // months later". Measuring from the first SURVIVING year's start said the latter and cut the
  // term from 51 months to 48.
  //
  // 48 is in no year of this tender's 15/27/39/51 series, so the offer was filed under the
  // org-wide 12/24/36/48 buckets instead: every weight became a plain whole year
  // (0.9973 / 1.9973 / 2.9973 / 3.9973) where the series calls for 1.2493 / 2.2493 / 3.2493 /
  // 4.2493. Origin's own arithmetic stayed exact — annual spend divided by weight matched the
  // client's report to the cent on all four terms — but every printed total was short, and the
  // Year 4 page, built on Origin's 48, dropped AGL for having no 48-month quote.
  //
  // Removing the LAST year still shortens the term, because nothing is priced past it any more:
  // the end moves, the start does not. One rule covers both.
  const firstStart = startOf(termData) ?? startOf(termData[years[0]]);
  const lastEnd    = endOf(termData[kept[kept.length - 1]]) ?? endOf(termData);

  const removingLastYear = yearKey === years[years.length - 1];

  let months: number | null = null;
  if (firstStart && lastEnd) months = inclusiveMonthSpan(firstStart.getTime(), lastEnd.getTime());
  if (months === null) {
    // No usable dates. Only the LAST year carries the term's end, so only removing it shortens
    // the term; removing any other year leaves both endpoints where they were.
    if (!removingLastYear) months = termMonths;
    else months = termMonths - Math.round(termMonths / years.length);
  }
  if (!(months > 0)) return null;

  const rebuilt: Record<string, any> = {};
  for (const [key, value] of Object.entries(termData)) {
    if (!/^year_\d+$/.test(key)) rebuilt[key] = value;       // rate_type, term-level date_range…
  }
  kept.forEach((k, index) => { rebuilt[`year_${index + 1}`] = termData[k]; });

  // ⚠️ THE REBASE HAS TO BE TOLD WHERE THE CONTRACT BEGINS.
  //
  // rebaseTermYearRanges anchors on termContractStart, which takes the earliest start it can
  // find. After renumbering, year_1 is the old year_2 — Origin's 01/01/2027 — so with the
  // term-level range deleted the whole term would be rebased three months late and come out
  // 48 months again by a different route. Stamping the term's real start back on keeps the
  // anchor where the contract actually starts, and the rebase then extends the new year_1
  // back to cover the period the removed stub used to hold.
  if (firstStart) {
    const camel = rebuilt.dateRange !== undefined && rebuilt.date_range === undefined;
    const span = { start: firstStart, end: dayBefore(addMonths(firstStart, months)) };
    if (camel) {
      rebuilt.dateRange = { ...rebuilt.dateRange, startDate: toIso(span.start), endDate: toIso(span.end) };
    } else {
      rebuilt.date_range = { ...(rebuilt.date_range ?? {}), start_date: toIso(span.start), end_date: toIso(span.end) };
    }
  } else {
    // Nothing to anchor on — leave no range at all rather than invent one.
    if (rebuilt.date_range && typeof rebuilt.date_range === 'object') delete rebuilt.date_range;
    if (rebuilt.dateRange && typeof rebuilt.dateRange === 'object') delete rebuilt.dateRange;
  }

  return { months, termData: rebaseTermYearRanges(rebuilt, months) };
};

/**
 * The period a manually-added environmental Rate Year covers, from the offer's own contract.
 *
 * ⚠️ WITHOUT THIS A HAND-ADDED ENVIRONMENTAL ROW CANNOT RESOLVE ITS POWER %.
 *
 * A parsed environmental slot carries the date range the retailer quoted it against, and that
 * range is the only thing that says which CALENDAR year the row belongs to — which is what
 * resolvePowerPct looks the sys_config default up by. A row the reviewer adds by hand is born
 * empty, so it had no year, and resolvePowerPct (correctly) refuses to guess one: the wand
 * button filled 0 and the row stayed blank.
 *
 * The date is not actually missing, though — it is on the offer. Every term of an offer opens
 * on the same contract start, so Rate Year N runs from the (N-1)th anniversary of that start to
 * the day before the Nth. For a term starting 2027-02-01, Year 2 is 2028-02-01 -> 2029-01-31,
 * and 2028 is the year its default is configured against.
 *
 * DELIBERATELY NOT CLAMPED to any term's end. Environmental charges are stored once per rate
 * side and shared by every term the offer quotes, so there is no single term end to clamp to —
 * the shortest would truncate the row for the longer terms. Only the start decides the calendar
 * year (see extractCalendarYear), and an uncumulative row is resolved by its start.
 *
 * Returns null when no term carries a start date, in which case the caller leaves the row alone
 * rather than inventing a period — the same rule rebaseTermYearRanges follows.
 */
export const envYearRangeFromTerms = (
  contractTerms: Record<string, any> | null | undefined,
  yearIndex: number,
  /**
   * Year 1's real end, when the offer already states one. Supply it and the later years follow
   * on from it instead of assuming Year 1 was twelve months — see the note below.
   */
  year1End?: string | Date | null,
): { start_date: string; end_date: string } | null => {
  if (!contractTerms || typeof contractTerms !== 'object') return null;
  if (!Number.isFinite(yearIndex) || yearIndex < 1) return null;

  // The offer's contract start: the earliest any of its terms opens on.
  let start: Date | null = null;
  for (const termData of Object.values(contractTerms)) {
    const termStart = termContractStart(termData as Record<string, any>);
    if (termStart && (!start || termStart < start)) start = termStart;
  }
  if (!start) return null;

  /**
   * ⚠️ YEAR 2 BEGINS WHERE YEAR 1 ENDS — IT IS NOT ALWAYS MONTH 13.
   *
   * This used to place every year at `start + (n-1) x 12 months`, which is right only while
   * Year 1 is twelve months long. A tender whose Year 1 is longer breaks it, and the two
   * conventions then get MIXED on one offer: Year 1 carries the period the retailer actually
   * quoted while Years 2+ are generated as if it had been a year.
   *
   * SOUTH PACIFIC SEEDS (15 / 27 / 39 / 51 months from 01/10/2026), AGL:
   *     year_1  2026-10-01 -> 2027-12-31   (15 months, as quoted)
   *     year_2  2027-10-01 -> 2028-09-30   (generated here, from month 13)
   *
   * October to December 2027 falls in BOTH, so the per-year average counted that quarter twice
   * at Year 1's rate — the highest, since certificate prices fall year on year. Year 1 alone is
   * a single slot and matched the client exactly; every term from Year 2 on came out high
   * (27mo 5,910 against their 5,449). Shell, Energy Australia and Momentum never showed it
   * because their Year 1 IS twelve months, so both conventions agree.
   *
   * ⚠️ Nothing moves when Year 1 is twelve months: anchoring to its end lands on exactly the
   * same dates as `start + n x 12`, for every start date. Only a Year 1 of some other length
   * changes, which is the case that was wrong.
   */
  const statedYear1End = year1End == null ? null : asDate(year1End);
  if (statedYear1End && yearIndex >= 2) {
    const yearStart = addMonths(dayAfter(statedYear1End), (yearIndex - 2) * 12);
    return {
      start_date: toIso(yearStart),
      end_date: toIso(dayBefore(addMonths(yearStart, 12))),
    };
  }
  // Year 1 itself, when its end is known, is exactly the period that was quoted.
  if (statedYear1End && yearIndex === 1) {
    return { start_date: toIso(start), end_date: toIso(statedYear1End) };
  }

  return {
    start_date: toIso(addMonths(start, (yearIndex - 1) * 12)),
    end_date: toIso(dayBefore(addMonths(start, yearIndex * 12))),
  };
};
